import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Folder,
  Share2,
  Lock,
  Archive,
  Edit3,
  Upload,
  LayoutGrid,
  List,
  Download,
  Trash2,
  MoreVertical,
  FileText,
  ChevronRight,
  FolderPlus,
  Search,
  X,
  Vault,
  Info,
  Video,
  Play,
} from 'lucide-react';
import { vaultSphereAPI } from '../services/api';
import VaultSphereShareModal from '../components/VaultSphereShareModal';
import VaultSphereDetailsModal from '../components/VaultSphereDetailsModal';

type VaultSection = 'mes-fichiers' | 'partages' | 'teams-video' | 'vault' | 'archives';
type ViewMode = 'grid' | 'list';
type SortBy = 'name' | 'date' | 'size' | 'type';
type PermissionLevel = 'private' | 'shared' | 'public' | 'vault';

interface FileItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  mimeType?: string;
  size?: number;
  updatedAt: string;
  permission: PermissionLevel;
  parentId: string | null;
  category?: string | null;
  subcategory?: string | null;
}

interface VaultCategory {
  id: string;
  label: string;
  icon: string;
  color: string;
  subcategories: { id: string; label: string }[];
}

const CATEGORY_COLORS: Record<string, string> = {
  indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  rose: 'bg-rose-100 text-rose-700 border-rose-200',
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
  sky: 'bg-sky-100 text-sky-700 border-sky-200',
};

type IconComponent = React.ComponentType<{ className?: string }>;

const PERMISSION_CONFIG: Record<PermissionLevel, { label: string; icon: IconComponent; color: string }> = {
  private: { label: 'Private', icon: Lock, color: 'bg-slate-100 text-slate-700 border-slate-200' },
  shared: { label: 'Shared', icon: Share2, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  public: { label: 'Public', icon: Globe, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  vault: { label: 'Coffre fort', icon: Vault, color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

function Globe(props: React.SVGAttributes<SVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

const DEMO_PIN = '1234';
const INACTIVITY_LOCK_MINUTES = 5;

const formatSize = (bytes?: number) => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const VaultSpherePage: React.FC = () => {
  const [section, setSection] = useState<VaultSection>('mes-fichiers');
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [vaultHasPin, setVaultHasPin] = useState<boolean | null>(null);
  const [pinDigits, setPinDigits] = useState<string[]>(['', '', '', '']);
  const [pinConfirmDigits, setPinConfirmDigits] = useState<string[]>(['', '', '', '']);
  const [pinSetupStep, setPinSetupStep] = useState<1 | 2>(1);
  const [pinError, setPinError] = useState(false);
  const [pinErrorMsg, setPinErrorMsg] = useState<string | null>(null);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isSettingPin, setIsSettingPin] = useState(false);
  const pinInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const pinConfirmRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [vaultLockMinutes, setVaultLockMinutes] = useState(INACTIVITY_LOCK_MINUTES);
  const [vaultSecondsLeft, setVaultSecondsLeft] = useState<number | null>(null);

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortBy, setSortBy] = useState<SortBy>('name');
  const [filterPermission, setFilterPermission] = useState<PermissionLevel | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<{ id: string | null; name: string }[]>([{ id: null, name: 'My files' }]);
  const [items, setItems] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderCategory, setNewFolderCategory] = useState('');
  const [newFolderSubcategory, setNewFolderSubcategory] = useState('');
  const [categories, setCategories] = useState<VaultCategory[]>([]);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubcategory, setFilterSubcategory] = useState('');
  const [folderCategoryMenuId, setFolderCategoryMenuId] = useState<string | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [permissionSubMenuId, setPermissionSubMenuId] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const folderInputRef = React.useRef<HTMLInputElement>(null);
  const [shareModalItem, setShareModalItem] = useState<FileItem | null>(null);
  const [detailsModalItem, setDetailsModalItem] = useState<FileItem | null>(null);
  const [teamsVideoFolderId, setTeamsVideoFolderId] = useState<string | null>(null);
  const [videoPlayerItem, setVideoPlayerItem] = useState<FileItem | null>(null);
  const [videoPlayerUrl, setVideoPlayerUrl] = useState<string | null>(null);
  const [videoPlayerLoading, setVideoPlayerLoading] = useState(false);
  const [showForgotPin, setShowForgotPin] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetNewPin, setResetNewPin] = useState<string[]>(['', '', '', '']);
  const [resetConfirmPin, setResetConfirmPin] = useState<string[]>(['', '', '', '']);
  const [isResettingPin, setIsResettingPin] = useState(false);

  const sidebarItems: { id: VaultSection; label: string; icon: IconComponent }[] = [
    { id: 'mes-fichiers', label: 'My files', icon: Folder },
    { id: 'partages', label: 'Shared with me', icon: Share2 },
    { id: 'teams-video', label: 'Teams video', icon: Video },
    { id: 'vault', label: 'Secure vault', icon: Vault },
    { id: 'archives', label: 'Archives', icon: Archive },
  ];

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const parentId = (section === 'mes-fichiers' || section === 'vault' || section === 'teams-video') ? currentFolderId : undefined;
      const params: { section?: string; parentFolderId?: string; category?: string; subcategory?: string } = { section, parentFolderId: parentId ?? undefined };
      if (!parentId && filterCategory) {
        params.category = filterCategory;
        if (filterSubcategory) params.subcategory = filterSubcategory;
      }
      const res = await vaultSphereAPI.getItems(params);
      const data = res.data?.data ?? [];
      if (section === 'teams-video' && res.data?.teamsVideoFolderId) {
        setTeamsVideoFolderId(res.data.teamsVideoFolderId);
      }
      setItems(
        data.map((x: { id: string; name: string; type: string; updatedAt: string; permission: string; parentId?: string | null; mimeType?: string; size?: number; category?: string; subcategory?: string }) => ({
          id: x.id,
          name: x.name,
          type: x.type as 'file' | 'folder',
          mimeType: x.mimeType,
          size: x.size,
          updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : new Date(x.updatedAt).toISOString(),
          permission: (x.permission || 'private') as PermissionLevel,
          parentId: x.parentId ?? null,
          category: x.category ?? null,
          subcategory: x.subcategory ?? null,
        }))
      );
    } catch (err: unknown) {
      setError(err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
        ? (err as { response: { data: { error: string } } }).response.data.error
        : 'Unable to load items.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [section, currentFolderId, filterCategory, filterSubcategory]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (section === 'partages') {
      setCurrentFolderId(null);
      setBreadcrumb([{ id: null, name: 'Shared with me' }]);
    } else if (section === 'teams-video') {
      setCurrentFolderId(null);
      setBreadcrumb([{ id: null, name: 'Teams video' }]);
    } else if (section !== 'mes-fichiers') {
      setCurrentFolderId(null);
      setBreadcrumb([{ id: null, name: 'My files' }]);
    }
  }, [section]);

  useEffect(() => {
    vaultSphereAPI.getCategories()
      .then((res) => setCategories((res.data?.data ?? []) as VaultCategory[]))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (section === 'vault' && !vaultUnlocked) {
      vaultSphereAPI.vaultHasPin()
        .then((res) => setVaultHasPin(res.data?.data?.hasPin ?? false))
        .catch(() => setVaultHasPin(false));
    }
  }, [section, vaultUnlocked]);

  useEffect(() => {
    if (!menuOpenId) return;
    const closeOnClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-vault-menu]')) {
        setMenuOpenId(null);
        setPermissionSubMenuId(null);
        setFolderCategoryMenuId(null);
      }
    };
    document.addEventListener('click', closeOnClickOutside, true);
    return () => document.removeEventListener('click', closeOnClickOutside, true);
  }, [menuOpenId]);

  const filteredAndSortedItems = items
    .filter((i) => (filterPermission === 'all' ? true : i.permission === filterPermission))
    .filter((i) => !searchQuery || i.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'date') return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      if (sortBy === 'size') return (b.size ?? 0) - (a.size ?? 0);
      if (sortBy === 'type') return (a.mimeType ?? '').localeCompare(b.mimeType ?? '');
      return 0;
    });

  const activeDigits = pinSetupStep === 2 ? pinConfirmDigits : pinDigits;
  const pinProgress = Math.min(activeDigits.filter(Boolean).length / 4, 1) * 100;

  const handlePinDigitChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...pinDigits];
    newDigits[index] = value.slice(-1);
    setPinDigits(newDigits);
    setPinError(false);
    setPinErrorMsg(null);
    if (value && index < 3) pinInputRefs.current[index + 1]?.focus();
    if (newDigits.every(Boolean)) {
      const enteredPin = newDigits.join('');
      if (vaultHasPin) {
        setIsUnlocking(true);
        vaultSphereAPI.vaultVerifyPin(enteredPin)
          .then(() => {
            setPinError(false);
            setPinDigits(['', '', '', '']);
            setVaultUnlocked(true);
            setVaultSecondsLeft(vaultLockMinutes * 60);
          })
          .catch((err: unknown) => {
            const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Code incorrect';
            setPinError(true);
            setPinErrorMsg(msg);
            setPinDigits(['', '', '', '']);
            pinInputRefs.current[0]?.focus();
          })
          .finally(() => setIsUnlocking(false));
      } else {
        setPinSetupStep(2);
        setPinConfirmDigits(['', '', '', '']);
        setTimeout(() => pinConfirmRefs.current[0]?.focus(), 50);
      }
    }
  };

  const handlePinConfirmChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...pinConfirmDigits];
    newDigits[index] = value.slice(-1);
    setPinConfirmDigits(newDigits);
    setPinError(false);
    setPinErrorMsg(null);
    if (value && index < 3) pinConfirmRefs.current[index + 1]?.focus();
    if (newDigits.every(Boolean)) {
      const confirmPin = newDigits.join('');
      const originalPin = pinDigits.join('');
      if (confirmPin !== originalPin) {
        setPinError(true);
        setPinErrorMsg('Les codes ne correspondent pas.');
        setPinConfirmDigits(['', '', '', '']);
        pinConfirmRefs.current[0]?.focus();
        return;
      }
      setIsSettingPin(true);
      vaultSphereAPI.vaultSetPin(originalPin)
        .then(() => {
          setVaultHasPin(true);
          setPinDigits(['', '', '', '']);
          setPinConfirmDigits(['', '', '', '']);
          setPinSetupStep(1);
          setVaultUnlocked(true);
          setVaultSecondsLeft(vaultLockMinutes * 60);
        })
        .catch((err: unknown) => {
          const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Error setting the PIN.';
          setPinError(true);
          setPinErrorMsg(msg);
        })
        .finally(() => setIsSettingPin(false));
    }
  };

  const handlePinSetupBack = () => {
    setPinSetupStep(1);
    setPinDigits(['', '', '', '']);
    setPinConfirmDigits(['', '', '', '']);
    setPinError(false);
    setPinErrorMsg(null);
    pinInputRefs.current[0]?.focus();
  };

  const handleForgotPinBack = () => {
    setShowForgotPin(false);
    setResetPassword('');
    setResetNewPin(['', '', '', '']);
    setResetConfirmPin(['', '', '', '']);
    setPinError(false);
    setPinErrorMsg(null);
  };

  const handleResetPinSubmit = () => {
    if (!resetPassword.trim()) {
      setPinError(true);
      setPinErrorMsg('Entrez votre mot de passe.');
      return;
    }
    const newPin = resetNewPin.join('');
    const confirmPin = resetConfirmPin.join('');
    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      setPinError(true);
      setPinErrorMsg('The new code must be 4 digits.');
      return;
    }
    if (newPin !== confirmPin) {
      setPinError(true);
      setPinErrorMsg('Les codes ne correspondent pas.');
      return;
    }
    setIsResettingPin(true);
    setPinError(false);
    vaultSphereAPI.vaultResetPin(resetPassword, newPin)
      .then(() => {
        handleForgotPinBack();
        setVaultUnlocked(true);
        setVaultSecondsLeft(vaultLockMinutes * 60);
      })
      .catch((err: unknown) => {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Error while resetting.';
        setPinError(true);
        setPinErrorMsg(msg);
      })
      .finally(() => setIsResettingPin(false));
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      pinInputRefs.current[index - 1]?.focus();
      const newDigits = [...pinDigits];
      newDigits[index - 1] = '';
      setPinDigits(newDigits);
    }
  };

  const handlePinConfirmKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !pinConfirmDigits[index] && index > 0) {
      pinConfirmRefs.current[index - 1]?.focus();
      const newDigits = [...pinConfirmDigits];
      newDigits[index - 1] = '';
      setPinConfirmDigits(newDigits);
    }
  };

  const handleLockVault = () => {
    setVaultUnlocked(false);
    setVaultSecondsLeft(null);
    setShowForgotPin(false);
  };

  useEffect(() => {
    if (section === 'vault' && !vaultUnlocked && !isUnlocking && !isSettingPin && vaultHasPin !== null) {
      const t = setTimeout(() => {
        if (pinSetupStep === 2) pinConfirmRefs.current[0]?.focus();
        else pinInputRefs.current[0]?.focus();
      }, 100);
      return () => clearTimeout(t);
    }
  }, [section, vaultUnlocked, isUnlocking, isSettingPin, vaultHasPin, pinSetupStep]);

  useEffect(() => {
    if (!vaultUnlocked || vaultSecondsLeft === null) return;
    if (vaultSecondsLeft <= 0) {
      setVaultUnlocked(false);
      setVaultSecondsLeft(null);
      return;
    }
    const t = setInterval(() => setVaultSecondsLeft((s) => (s == null ? null : Math.max(0, s - 1))), 1000);
    return () => clearInterval(t);
  }, [vaultUnlocked, vaultSecondsLeft]);

  const isVaultContext = section === 'vault' && vaultUnlocked;
  const uploadPermission: PermissionLevel = isVaultContext ? 'vault' : 'private';
  const uploadIsInVault = isVaultContext;

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const fileList = e.dataTransfer.files;
      const allowed = section === 'mes-fichiers' || section === 'teams-video' || (section === 'vault' && vaultUnlocked);
      if (!fileList.length || !allowed) return;
      setError(null);
      setUploading(true);
      setUploadProgress(0);
      const files = Array.from(fileList);
      let done = 0;
      const total = files.length;
      try {
        for (const file of files) {
          try {
            const formData = new FormData();
            formData.append('file', file);
            if (currentFolderId && (section === 'mes-fichiers' || section === 'teams-video')) formData.append('parentFolderId', currentFolderId);
            else if (section === 'teams-video' && teamsVideoFolderId) formData.append('parentFolderId', teamsVideoFolderId);
            formData.append('permission', uploadPermission);
            formData.append('isInVault', String(uploadIsInVault));
            await vaultSphereAPI.uploadFile(formData);
          } catch (err: unknown) {
            const msg = err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
              ? (err as { response: { data: { error: string } } }).response.data.error
              : 'Upload failed.';
            setError(msg);
          }
          done += 1;
          setUploadProgress(Math.round((done / total) * 100));
        }
        await fetchItems();
      } finally {
        setUploading(false);
        setUploadProgress(100);
      }
    },
    [currentFolderId, section, vaultUnlocked, uploadPermission, uploadIsInVault, fetchItems, teamsVideoFolderId]
  );

  const handleCreateFolder = async () => {
    const allowed = section === 'mes-fichiers' || section === 'teams-video' || (section === 'vault' && vaultUnlocked);
    if (!newFolderName.trim() || !allowed) return;
    setError(null);
    try {
      await vaultSphereAPI.createFolder({
        name: newFolderName.trim(),
        category: newFolderCategory || undefined,
        subcategory: newFolderSubcategory || undefined,
        parentFolderId: (section === 'mes-fichiers' ? currentFolderId : section === 'teams-video' ? (currentFolderId || teamsVideoFolderId) : null) ?? undefined,
        permission: uploadPermission,
        isInVault: uploadIsInVault,
      });
      setNewFolderName('');
      setNewFolderCategory('');
      setNewFolderSubcategory('');
      setShowNewFolder(false);
      await fetchItems();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
        ? (err as { response: { data: { error: string } } }).response.data.error
        : 'Unable to create the folder.';
      setError(msg);
    }
  };

  const handleDelete = async (item: FileItem) => {
    setError(null);
    try {
      if (item.type === 'folder') {
        await vaultSphereAPI.deleteFolder(item.id);
      } else {
        await vaultSphereAPI.deleteFile(item.id);
      }
      setMenuOpenId(null);
      await fetchItems();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
        ? (err as { response: { data: { error: string } } }).response.data.error
        : 'Impossible de supprimer.';
      setError(msg);
    }
  };

  const isVideoFile = (item: FileItem) => {
    if (item.type !== 'file') return false;
    const mime = (item.mimeType || '').toLowerCase();
    const name = (item.name || '').toLowerCase();
    return mime.startsWith('video/') || /\.(webm|mp4|ogg|mov|avi|mkv)$/.test(name);
  };

  const handleVideoView = async (item: FileItem) => {
    if (item.type !== 'file' || !isVideoFile(item)) return;
    setMenuOpenId(null);
    setVideoPlayerItem(item);
    setVideoPlayerUrl(null);
    setVideoPlayerLoading(true);
    setError(null);
    try {
      const res = await vaultSphereAPI.downloadFile(item.id);
      const data = res.data;
      if (data instanceof Blob && data.size > 0) {
        const url = window.URL.createObjectURL(data);
        setVideoPlayerUrl(url);
      } else {
        setError('Unable to load the video.');
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: unknown } }).response?.data === 'object'
        ? (err as { response: { data: { error?: string } } }).response?.data?.error
        : null;
      setError(msg || 'Unable to load the video.');
    } finally {
      setVideoPlayerLoading(false);
    }
  };

  const closeVideoPlayer = () => {
    if (videoPlayerUrl) window.URL.revokeObjectURL(videoPlayerUrl);
    setVideoPlayerItem(null);
    setVideoPlayerUrl(null);
  };

  const handleDownload = async (fileId: string) => {
    setError(null);
    try {
      const res = await vaultSphereAPI.downloadFile(fileId);
      const data = res.data;
      if (data instanceof Blob && data.size > 0) {
        const item = items.find((i) => i.type === 'file' && i.id === fileId);
        const name = item?.name ?? 'download';
        const url = window.URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        setError('Invalid server response.');
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: unknown } }).response?.data === 'object'
        ? (err as { response: { data: { error?: string } } }).response?.data?.error
        : null;
      setError(msg || 'Unable to download.');
    }
  };

  const handleFolderClick = (folderId: string, folderName: string) => {
    setCurrentFolderId(folderId);
    setBreadcrumb((prev) => [...prev, { id: folderId, name: folderName }]);
    setFilterCategory('');
    setFilterSubcategory('');
  };

  const handleBreadcrumbClick = (index: number) => {
    const b = breadcrumb[index];
    setCurrentFolderId(b.id);
    setBreadcrumb((prev) => prev.slice(0, index + 1));
    if (!b.id) setFilterCategory('');
    if (!b.id) setFilterSubcategory('');
  };

  const handleUpdateFolderCategory = async (item: FileItem, catId: string, subId: string) => {
    if (item.type !== 'folder') return;
    setFolderCategoryMenuId(null);
    setMenuOpenId(null);
    setError(null);
    try {
      await vaultSphereAPI.updateFolder(item.id, { category: catId || undefined, subcategory: subId || undefined });
      await fetchItems();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
        ? (err as { response: { data: { error: string } } }).response.data.error
        : 'Unable to update the category.';
      setError(msg);
    }
  };

  const getCategoryLabel = (catId: string, subId?: string | null) => {
    const cat = categories.find((c) => c.id === catId);
    if (!cat) return catId;
    const sub = subId ? cat.subcategories.find((s) => s.id === subId) : null;
    return sub ? `${cat.label} › ${sub.label}` : cat.label;
  };

  const handleUpdatePermission = async (item: FileItem, newPermission: PermissionLevel) => {
    setPermissionSubMenuId(null);
    setMenuOpenId(null);
    setError(null);
    try {
      if (item.type === 'folder') {
        await vaultSphereAPI.updateFolder(item.id, { permission: newPermission, isInVault: newPermission === 'vault' });
      } else {
        await vaultSphereAPI.updateFile(item.id, { permission: newPermission, isInVault: newPermission === 'vault' });
      }
      await fetchItems();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
        ? (err as { response: { data: { error: string } } }).response.data.error
        : 'Unable to update visibility.';
      setError(msg);
    }
  };

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawList = e.target.files;
      const files = rawList ? Array.from(rawList) : [];
      e.target.value = '';
      if (files.length === 0) {
        setError('No file selected.');
        return;
      }
      const allowed = section === 'mes-fichiers' || section === 'teams-video' || (section === 'vault' && vaultUnlocked);
      if (!allowed) {
        setError('Open "My files", "Teams video" or the "Vault" (unlocked) to send files.');
        return;
      }
      setError(null);
      setUploading(true);
      setUploadProgress(0);
      try {
        let done = 0;
        const total = files.length;
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          try {
            const formData = new FormData();
            formData.append('file', file);
            if (currentFolderId && (section === 'mes-fichiers' || section === 'teams-video')) formData.append('parentFolderId', currentFolderId);
            else if (section === 'teams-video' && teamsVideoFolderId) formData.append('parentFolderId', teamsVideoFolderId);
            formData.append('permission', uploadPermission);
            formData.append('isInVault', String(uploadIsInVault));
            await vaultSphereAPI.uploadFile(formData);
          } catch (err: unknown) {
            const msg = err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
              ? (err as { response: { data: { error: string } } }).response.data.error
              : 'Upload failed.';
            setError(msg);
          }
          done += 1;
          setUploadProgress(Math.round((done / total) * 100));
        }
        await fetchItems();
      } finally {
        setUploading(false);
        setUploadProgress(100);
      }
    },
    [currentFolderId, section, vaultUnlocked, uploadPermission, uploadIsInVault, fetchItems, teamsVideoFolderId]
  );

  const handleFolderSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawList = e.target.files;
      const files = rawList ? Array.from(rawList) : [];
      e.target.value = '';
      if (files.length === 0) {
        setError('No folder selected.');
        return;
      }
      const allowed = section === 'mes-fichiers' || section === 'teams-video' || (section === 'vault' && vaultUnlocked);
      if (!allowed) {
        setError('Open "My files" or the "Vault" (unlocked) to send a folder.');
        return;
      }
      setError(null);
      setUploading(true);
      setUploadProgress(0);
      try {
        const pathToFolderId: Record<string, string> = {};
        const dirPaths = new Set<string>();
        files.forEach((f) => {
          const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
          const parts = rel.split('/');
          if (parts.length > 1) {
            for (let i = 1; i < parts.length; i++) {
              dirPaths.add(parts.slice(0, i).join('/'));
            }
          }
        });
        const sortedDirs = Array.from(dirPaths).sort((a, b) => a.split('/').length - b.split('/').length);
        for (const dirPath of sortedDirs) {
          const parts = dirPath.split('/');
          const name = parts[parts.length - 1];
          const parentPath = parts.length > 1 ? parts.slice(0, -1).join('/') : '';
          const parentId = parentPath ? pathToFolderId[parentPath] : (section === 'teams-video' ? (currentFolderId || teamsVideoFolderId) : currentFolderId) ?? undefined;
          try {
            const res = await vaultSphereAPI.createFolder({ name, parentFolderId: parentId ?? undefined, permission: uploadPermission, isInVault: uploadIsInVault });
            const id = (res.data as { data?: { id?: string } })?.data?.id;
            if (id) pathToFolderId[dirPath] = id;
          } catch (_) {
            setError('Failed to create a folder.');
          }
        }
        let done = 0;
        const total = files.length;
        for (const file of files) {
          const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
          const parts = rel.split('/');
          const parentPath = parts.length > 1 ? parts.slice(0, -1).join('/') : '';
          const parentId = parentPath ? pathToFolderId[parentPath] : (section === 'teams-video' ? (currentFolderId || teamsVideoFolderId) : currentFolderId) ?? undefined;
          try {
            const formData = new FormData();
            formData.append('file', file);
            if (parentId) formData.append('parentFolderId', parentId);
            formData.append('permission', uploadPermission);
            formData.append('isInVault', String(uploadIsInVault));
            await vaultSphereAPI.uploadFile(formData);
          } catch (_) {
            setError('Failed to upload one or more files.');
          }
          done += 1;
          setUploadProgress(Math.round((done / total) * 100));
        }
        await fetchItems();
      } finally {
        setUploading(false);
        setUploadProgress(100);
      }
    },
    [currentFolderId, section, vaultUnlocked, uploadPermission, uploadIsInVault, fetchItems, teamsVideoFolderId]
  );

  const renderPermissionBadge = (permission: PermissionLevel) => {
    const config = PERMISSION_CONFIG[permission];
    const Icon = config.icon as IconComponent;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  const renderCategoryBadge = (item: FileItem) => {
    if (item.type !== 'folder' || !item.category) return null;
    const cat = categories.find((c) => c.id === item.category);
    if (!cat) return null;
    const colorClass = CATEGORY_COLORS[cat.color] ?? 'bg-slate-100 text-slate-700 border-slate-200';
    const sub = item.subcategory ? cat.subcategories.find((s) => s.id === item.subcategory) : null;
    const label = sub ? sub.label : cat.label;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${colorClass}`} title={cat.label}>
        {label}
      </span>
    );
  };

  return (
    <div className="flex-1 min-h-0 w-full flex flex-col bg-[#f8fafc]">
      {/* Header compact type Drive/Dropbox */}
      <header className="flex-shrink-0 h-14 px-4 md:px-6 flex items-center gap-3 border-b border-slate-200/80 bg-white shadow-sm">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center flex-shrink-0">
          <Vault className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-slate-900 truncate">Secure storage</h1>
          <p className="text-xs text-slate-500 truncate hidden sm:block">Files, shares and vault</p>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        <aside className="w-48 flex-shrink-0 border-r border-slate-200/80 bg-white py-4">
          <nav className="px-2 space-y-0.5">
            {sidebarItems.map((item) => {
              const isVault = item.id === 'vault';
              const isActive = section === item.id;
              const ButtonComponent = isVault ? motion.button : 'button';
              const buttonProps = isVault
                ? { whileHover: { x: 4 } as const, whileTap: { scale: 0.98 } as const, transition: { type: 'spring' as const, stiffness: 400, damping: 17 } }
                : {};
              return (
                <ButtonComponent
                  key={item.id}
                  onClick={() => setSection(item.id)}
                  {...buttonProps}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isVault && isActive
                      ? 'bg-gradient-to-r from-vault-gold via-vault-light to-vault-dark text-slate-900 shadow-md'
                      : isVault
                        ? 'text-slate-700 hover:bg-slate-50/80'
                        : isActive
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  {isVault ? (
                    <span className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-white/30' : 'bg-amber-100'}`}>
                      <Vault className={`w-3.5 h-3.5 ${isActive ? 'text-slate-900' : 'text-amber-700'}`} />
                    </span>
                  ) : (
                    (() => {
                      const SidebarIcon = item.icon;
                      return <SidebarIcon className="w-4 h-4 flex-shrink-0 opacity-80" />;
                    })()
                  )}
                  <span className="truncate">{item.label}</span>
                  {isVault && !isActive && (
                    <span className="ml-auto flex-shrink-0" title="Protected vault">
                      <Lock className="w-4 h-4 text-vault-gold" />
                    </span>
                  )}
                  {isVault && isActive && <span className="ml-auto flex-shrink-0 w-1.5 h-1.5 rounded-full bg-white/80" title="Coffre fort" />}
                </ButtonComponent>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {/* Inputs toujours montés pour que l’événement change soit bien reçu après sélection */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
            accept="*"
          />
          <input
            ref={folderInputRef}
            type="file"
            {...({ webkitdirectory: '', directory: '', multiple: true } as React.InputHTMLAttributes<HTMLInputElement>)}
            className="hidden"
            onChange={handleFolderSelect}
          />
          {(section === 'mes-fichiers' || section === 'teams-video') && (
            <>
              {/* Toolbar type Drive/Dropbox */}
              <div className="flex-shrink-0 px-4 py-2.5 border-b border-slate-200/80 bg-white flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 text-sm text-slate-600 min-w-0">
                  {breadcrumb.map((b, i) => (
                    <React.Fragment key={b.id ?? 'root'}>
                      <button type="button" onClick={() => handleBreadcrumbClick(i)} className="hover:text-slate-900 font-medium truncate max-w-[120px] sm:max-w-none">
                        {b.name}
                      </button>
                      {i < breadcrumb.length - 1 && <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                    </React.Fragment>
                  ))}
                </div>
                <div className="flex-1 min-w-0" />
                <div className="relative flex-shrink-0">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm w-40 sm:w-48 focus:ring-2 focus:ring-slate-300 focus:border-slate-400" />
                </div>
                {!currentFolderId && section === 'mes-fichiers' && (
                  <>
                    <select value={filterCategory} onChange={(e) => { setFilterCategory(e.target.value); setFilterSubcategory(''); }} className="py-1.5 px-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-300">
                      <option value="">All categories</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </select>
                    {filterCategory && (
                      <select value={filterSubcategory} onChange={(e) => setFilterSubcategory(e.target.value)} className="py-1.5 px-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-300">
                        <option value="">All subcategories</option>
                        {categories.find((c) => c.id === filterCategory)?.subcategories.map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>
                    )}
                  </>
                )}
                <select value={filterPermission} onChange={(e) => setFilterPermission(e.target.value as PermissionLevel | 'all')} className="py-1.5 px-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-300">
                  <option value="all">Visibility</option>
                  <option value="private">Private</option>
                  <option value="shared">Shared</option>
                  <option value="public">Public</option>
                  <option value="vault">Vault</option>
                </select>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortBy)} className="py-1.5 px-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-300">
                  <option value="name">Name</option>
                  <option value="date">Date</option>
                  <option value="size">Size</option>
                  <option value="type">Type</option>
                </select>
                <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                  <button type="button" onClick={() => setViewMode('grid')} className={`p-1.5 ${viewMode === 'grid' ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:bg-slate-50'}`} title="Grid"><LayoutGrid className="w-4 h-4" /></button>
                  <button type="button" onClick={() => setViewMode('list')} className={`p-1.5 ${viewMode === 'list' ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:bg-slate-50'}`} title="List"><List className="w-4 h-4" /></button>
                </div>
                <button type="button" onClick={() => setShowNewFolder(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors">
                  <FolderPlus className="w-4 h-4" />
                  New folder
                </button>
              </div>

              {/* Upload zone + list */}
              <div
                className={`flex-1 overflow-auto p-4 md:p-6 transition-colors ${isDragging ? 'bg-slate-100' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center justify-between gap-2">
                    <span>{error}</span>
                    <button type="button" onClick={() => setError(null)} className="p-1 rounded hover:bg-red-100 text-red-600" aria-label="Close">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {loading && (
                  <div className="mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200 text-sm text-slate-600">
                    Loading...
                  </div>
                )}
                {uploading && (
                  <div className="mb-4 flex items-center gap-3 py-2 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-600">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-slate-600 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} /></div>
                    <span>{uploadProgress}%</span>
                  </div>
                )}

                <div
                  role="button"
                  tabIndex={0}
                  className={`border-2 border-dashed rounded-xl p-6 text-center mb-6 transition-colors cursor-pointer ${
                    isDragging ? 'border-slate-400 bg-slate-100' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50'
                  }`}
                  onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button')) return;
                    fileInputRef.current?.click();
                  }}
                >
                  <Upload className="w-9 h-9 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Drag & drop files or click to browse</p>
                  <div className="flex flex-wrap justify-center gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200">
                      Fichiers
                    </button>
                    <button type="button" onClick={() => folderInputRef.current?.click()} className="px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200">
                      Dossier
                    </button>
                  </div>
                </div>

                {showNewFolder && (
                  <div className="mb-4 p-4 bg-white rounded-xl border border-slate-200 space-y-3">
                    <input
                      type="text"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      placeholder="Folder name"
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-full max-w-xs"
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                      autoFocus
                    />
                    <div className="flex flex-wrap gap-2 items-center">
                      <select
                        value={newFolderCategory}
                        onChange={(e) => { setNewFolderCategory(e.target.value); setNewFolderSubcategory(''); }}
                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      >
                        <option value="">Category (optional)</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                      {newFolderCategory && (
                        <select
                          value={newFolderSubcategory}
                          onChange={(e) => setNewFolderSubcategory(e.target.value)}
                          className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        >
                          <option value="">Subcategory</option>
                          {categories.find((c) => c.id === newFolderCategory)?.subcategories.map((s) => (
                            <option key={s.id} value={s.id}>{s.label}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={handleCreateFolder} className="px-3 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700">Create</button>
                      <button type="button" onClick={() => { setShowNewFolder(false); setNewFolderName(''); setNewFolderCategory(''); setNewFolderSubcategory(''); }} className="p-2 text-slate-500 hover:text-slate-700"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}

                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {filteredAndSortedItems.map((item) => (
                      <div
                        key={item.id}
                        className="relative group bg-white rounded-lg border border-slate-200/80 p-3 hover:border-slate-300 hover:shadow-sm transition-all"
                      >
                        {item.type === 'folder' ? (
                          <button
                            type="button"
                            onClick={() => item.type === 'folder' && handleFolderClick(item.id, item.name)}
                            className="w-full text-left"
                          >
                            <Folder className="w-12 h-12 text-amber-500 mb-2" />
                            <p className="font-medium text-slate-900 truncate">{item.name}</p>
                          </button>
                        ) : (
                          <div
                            role={isVideoFile(item) ? 'button' : undefined}
                            tabIndex={isVideoFile(item) ? 0 : undefined}
                            className={isVideoFile(item) ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500/50 rounded' : ''}
                            onDoubleClick={() => item.type === 'file' && isVideoFile(item) && handleVideoView(item)}
                            onKeyDown={(e) => isVideoFile(item) && e.key === 'Enter' && handleVideoView(item)}
                            title={isVideoFile(item) ? 'Double-clic pour regarder' : undefined}
                          >
                            {isVideoFile(item) ? <Video className="w-12 h-12 text-emerald-500 mb-2" /> : <FileText className="w-12 h-12 text-slate-400 mb-2" />}
                            <p className="font-medium text-slate-900 truncate">{item.name}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{formatSize(item.size)} • {formatDate(item.updatedAt)}</p>
                          </div>
                        )}
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-1">
                          <div className="flex flex-wrap gap-1">
                            {renderPermissionBadge(item.permission)}
                            {item.type === 'folder' && renderCategoryBadge(item)}
                          </div>
                          <div className="relative" data-vault-menu>
                            <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === item.id ? null : item.id); }} className="p-1 rounded hover:bg-slate-100">
                              <MoreVertical className="w-4 h-4 text-slate-500" />
                            </button>
                            {menuOpenId === item.id && (
                                <div className="absolute right-0 top-full mt-1 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-[180px]">
                                  <button type="button" onClick={() => { setMenuOpenId(null); setShareModalItem(item); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Info className="w-4 h-4" /> Details / Access</button>
                                  {item.type === 'file' && isVideoFile(item) && (
                                    <button type="button" onClick={() => handleVideoView(item)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Play className="w-4 h-4" /> Regarder</button>
                                  )}
                                  <button type="button" onClick={() => item.type === 'file' && handleDownload(item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Download className="w-4 h-4" /> Download</button>
                                  <div className="relative">
                                    <button type="button" onClick={() => setPermissionSubMenuId(permissionSubMenuId === item.id ? null : item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Lock className="w-4 h-4" /> Change visibility</button>
                                    {permissionSubMenuId === item.id && (
                                      <div className="absolute left-full top-0 ml-0.5 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-30 min-w-[140px]">
                                        {(['private', 'shared', 'public', 'vault'] as PermissionLevel[]).map((perm) => (
                                          <button key={perm} type="button" onClick={() => handleUpdatePermission(item, perm)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left">
                                            {renderPermissionBadge(perm)}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  {item.type === 'folder' && (
                                    <div className="relative">
                                      <button type="button" onClick={() => setFolderCategoryMenuId(folderCategoryMenuId === item.id ? null : item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Folder className="w-4 h-4" /> Category</button>
                                      {folderCategoryMenuId === item.id && (
                                        <div className="absolute left-full top-0 ml-0.5 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-30 min-w-[180px] max-h-64 overflow-y-auto">
                                          <button type="button" onClick={() => handleUpdateFolderCategory(item, '', '')} className="w-full px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 text-left">Aucune</button>
                                          {categories.map((cat) => (
                                            <div key={cat.id}>
                                              <button type="button" onClick={() => handleUpdateFolderCategory(item, cat.id, '')} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left font-medium">{cat.label}</button>
                                              {cat.subcategories.map((sub) => (
                                                <button key={sub.id} type="button" onClick={() => handleUpdateFolderCategory(item, cat.id, sub.id)} className="w-full pl-6 pr-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 text-left">— {sub.label}</button>
                                              ))}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  <button type="button" onClick={() => { setMenuOpenId(null); setShareModalItem(item); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Share2 className="w-4 h-4" /> Partager</button>
                                  <button type="button" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Edit3 className="w-4 h-4" /> Renommer</button>
                                  <button type="button" onClick={() => handleDelete(item)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left"><Trash2 className="w-4 h-4" /> Supprimer</button>
                                </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left py-3 px-4 font-semibold text-slate-700">Name</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-700">Category</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-700">Visibility</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-700">Size</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-700">Modified</th>
                          <th className="w-10" />
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAndSortedItems.map((item) => (
                          <tr
                            key={item.id}
                            className={`border-b border-slate-100 hover:bg-slate-50/50 ${item.type === 'file' && isVideoFile(item) ? 'cursor-pointer' : ''}`}
                            onDoubleClick={() => item.type === 'file' && isVideoFile(item) && handleVideoView(item)}
                          >
                            <td className="py-3 px-4">
                              {item.type === 'folder' ? (
                                <button type="button" onClick={() => handleFolderClick(item.id, item.name)} className="flex items-center gap-2 font-medium text-slate-900 hover:text-emerald-600">
                                  <Folder className="w-4 h-4 text-amber-500" />
                                  {item.name}
                                </button>
                              ) : (
                                <span className="flex items-center gap-2 text-slate-900">
                                  {isVideoFile(item) ? <Video className="w-4 h-4 text-emerald-500" /> : <FileText className="w-4 h-4 text-slate-400" />}
                                  {item.name}
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4">{item.type === 'folder' ? renderCategoryBadge(item) || '—' : '—'}</td>
                            <td className="py-3 px-4">{renderPermissionBadge(item.permission)}</td>
                            <td className="py-3 px-4 text-slate-600">{formatSize(item.size)}</td>
                            <td className="py-3 px-4 text-slate-600">{formatDate(item.updatedAt)}</td>
                            <td className="py-3 px-4">
                              <div className="relative" data-vault-menu>
                                <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === item.id ? null : item.id); }} className="p-1 rounded hover:bg-slate-200">
                                  <MoreVertical className="w-4 h-4 text-slate-500" />
                                </button>
                                {menuOpenId === item.id && (
                                  <div className="absolute right-0 top-full mt-1 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-[180px]">
                                      <button type="button" onClick={() => { setMenuOpenId(null); setShareModalItem(item); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Info className="w-4 h-4" /> Details / Access</button>
                                      {item.type === 'file' && isVideoFile(item) && (
                                        <button type="button" onClick={() => handleVideoView(item)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Play className="w-4 h-4" /> Regarder</button>
                                      )}
                                      <button type="button" onClick={() => item.type === 'file' && handleDownload(item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Download className="w-4 h-4" /> Download</button>
                                      <div className="relative">
                                        <button type="button" onClick={() => setPermissionSubMenuId(permissionSubMenuId === item.id ? null : item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Lock className="w-4 h-4" /> Change visibility</button>
                                        {permissionSubMenuId === item.id && (
                                          <div className="absolute left-full top-0 ml-0.5 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-30 min-w-[140px]">
                                            {(['private', 'shared', 'public', 'vault'] as PermissionLevel[]).map((perm) => (
                                              <button key={perm} type="button" onClick={() => handleUpdatePermission(item, perm)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left">
                                                {renderPermissionBadge(perm)}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      {item.type === 'folder' && (
                                        <div className="relative">
                                          <button type="button" onClick={() => setFolderCategoryMenuId(folderCategoryMenuId === item.id ? null : item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Folder className="w-4 h-4" /> Category</button>
                                          {folderCategoryMenuId === item.id && (
                                            <div className="absolute left-full top-0 ml-0.5 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-30 min-w-[180px] max-h-64 overflow-y-auto">
                                            <button type="button" onClick={() => handleUpdateFolderCategory(item, '', '')} className="w-full px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 text-left">Aucune</button>
                                            {categories.map((cat) => (
                                              <div key={cat.id}>
                                                <button type="button" onClick={() => handleUpdateFolderCategory(item, cat.id, '')} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left font-medium">{cat.label}</button>
                                                {cat.subcategories.map((sub) => (
                                                  <button key={sub.id} type="button" onClick={() => handleUpdateFolderCategory(item, cat.id, sub.id)} className="w-full pl-6 pr-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 text-left">— {sub.label}</button>
                                                ))}
                                              </div>
                                            ))}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      <button type="button" onClick={() => { setMenuOpenId(null); setShareModalItem(item); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Share2 className="w-4 h-4" /> Partager</button>
                                      <button type="button" className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Edit3 className="w-4 h-4" /> Renommer</button>
                                      <button type="button" onClick={() => handleDelete(item)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left"><Trash2 className="w-4 h-4" /> Supprimer</button>
                                    </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {filteredAndSortedItems.length === 0 && (
                  <div className="text-center py-12 text-slate-500 text-sm">
                    {searchQuery || filterPermission !== 'all' ? 'No results.' : 'No files or folders. Drag & drop to upload.'}
                  </div>
                )}
              </div>
            </>
          )}

          {section === 'partages' && (
            <>
              <div className="flex-shrink-0 px-4 py-2.5 border-b border-slate-200/80 bg-white flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-slate-700">Shared with me</span>
                <span className="text-slate-400">/</span>
                <span className="text-sm text-slate-600">View and download only</span>
                <div className="flex-1 min-w-0" />
                <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                  <button type="button" onClick={() => setViewMode('grid')} className={`p-1.5 ${viewMode === 'grid' ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:bg-slate-50'}`} title="Grid"><LayoutGrid className="w-4 h-4" /></button>
                  <button type="button" onClick={() => setViewMode('list')} className={`p-1.5 ${viewMode === 'list' ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:bg-slate-50'}`} title="List"><List className="w-4 h-4" /></button>
                </div>
              </div>
              <div
                className={`flex-1 overflow-auto p-4 md:p-6 transition-colors ${isDragging ? 'bg-slate-100' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center justify-between gap-2">
                    <span>{error}</span>
                    <button type="button" onClick={() => setError(null)} className="p-1 rounded hover:bg-red-100 text-red-600" aria-label="Close"><X className="w-4 h-4" /></button>
                  </div>
                )}
                {loading && (
                  <div className="mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200 text-sm text-slate-600">Loading...</div>
                )}
                {!loading && !error && filteredAndSortedItems.length === 0 && (
                  <div className="py-12 text-center text-slate-500 text-sm rounded-xl bg-slate-50 border border-dashed border-slate-200">
                    No files shared with you.
                  </div>
                )}
                {!loading && filteredAndSortedItems.length > 0 && viewMode === 'grid' && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {filteredAndSortedItems.map((item) => (
                      <div key={item.id} className="relative group bg-white rounded-lg border border-slate-200/80 p-3 hover:border-slate-300 hover:shadow-sm transition-all">
                        {item.type === 'folder' ? (
                          <button type="button" onClick={() => item.type === 'folder' && setDetailsModalItem(item)} className="w-full text-left">
                            <Folder className="w-12 h-12 text-amber-500 mb-2" />
                            <p className="font-medium text-slate-900 truncate">{item.name}</p>
                          </button>
                        ) : (
                          <div
                            role={isVideoFile(item) ? 'button' : undefined}
                            tabIndex={isVideoFile(item) ? 0 : undefined}
                            className={isVideoFile(item) ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500/50 rounded' : ''}
                            onDoubleClick={() => item.type === 'file' && isVideoFile(item) && handleVideoView(item)}
                            onKeyDown={(e) => isVideoFile(item) && e.key === 'Enter' && handleVideoView(item)}
                            title={isVideoFile(item) ? 'Double-clic pour regarder' : undefined}
                          >
                            {isVideoFile(item) ? <Video className="w-12 h-12 text-emerald-500 mb-2" /> : <FileText className="w-12 h-12 text-slate-400 mb-2" />}
                            <p className="font-medium text-slate-900 truncate">{item.name}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{formatSize(item.size)} • {formatDate(item.updatedAt)}</p>
                          </div>
                        )}
                        <div className="mt-2 flex items-center justify-between">
                          {renderPermissionBadge(item.permission)}
                          <div className="relative" data-vault-menu>
                            <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === item.id ? null : item.id); }} className="p-1 rounded hover:bg-slate-100">
                              <MoreVertical className="w-4 h-4 text-slate-500" />
                            </button>
                            {menuOpenId === item.id && (
                              <div className="absolute right-0 top-full mt-1 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-[180px]">
                                <button type="button" onClick={() => { setMenuOpenId(null); setDetailsModalItem(item); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Info className="w-4 h-4" /> Details</button>
                                {item.type === 'file' && isVideoFile(item) && (
                                  <button type="button" onClick={() => handleVideoView(item)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Play className="w-4 h-4" /> Regarder</button>
                                )}
                                <button type="button" onClick={() => item.type === 'file' && handleDownload(item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Download className="w-4 h-4" /> Download</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {!loading && filteredAndSortedItems.length > 0 && viewMode === 'list' && (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left py-3 px-4 font-semibold text-slate-700">Name</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-700">Visibility</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-700">Size</th>
                          <th className="text-left py-3 px-4 font-semibold text-slate-700">Modified</th>
                          <th className="w-10" />
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAndSortedItems.map((item) => (
                          <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                            <td className="py-3 px-4">
                              {item.type === 'folder' ? (
                                <button type="button" onClick={() => setDetailsModalItem(item)} className="flex items-center gap-2 font-medium text-slate-900 hover:text-emerald-600">
                                  <Folder className="w-4 h-4 text-amber-500" />
                                  {item.name}
                                </button>
                              ) : (
                                <span className="flex items-center gap-2 text-slate-900">
                                  {isVideoFile(item) ? <Video className="w-4 h-4 text-emerald-500" /> : <FileText className="w-4 h-4 text-slate-400" />}
                                  {item.name}
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4">{renderPermissionBadge(item.permission)}</td>
                            <td className="py-3 px-4 text-slate-600">{formatSize(item.size)}</td>
                            <td className="py-3 px-4 text-slate-600">{formatDate(item.updatedAt)}</td>
                            <td className="py-3 px-4">
                              <div className="relative" data-vault-menu>
                                <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === item.id ? null : item.id); }} className="p-1 rounded hover:bg-slate-200">
                                  <MoreVertical className="w-4 h-4 text-slate-500" />
                                </button>
                                {menuOpenId === item.id && (
                                  <div className="absolute right-0 top-full mt-1 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-[180px]">
                                    <button type="button" onClick={() => { setMenuOpenId(null); setDetailsModalItem(item); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Info className="w-4 h-4" /> Details</button>
                                    {item.type === 'file' && isVideoFile(item) && (
                                      <button type="button" onClick={() => handleVideoView(item)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Play className="w-4 h-4" /> Regarder</button>
                                    )}
                                    <button type="button" onClick={() => item.type === 'file' && handleDownload(item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Download className="w-4 h-4" /> Download</button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {section === 'vault' && (
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <AnimatePresence mode="wait">
                {!vaultUnlocked ? (
                  /* Écran de verrouillage premium */
                  <motion.div
                    key="vault-lock"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -20, transition: { duration: 0.4 } }}
                    transition={{ duration: 0.3 }}
                    className="flex-1 overflow-auto flex items-center justify-center p-6 relative min-h-[400px] bg-slate-50"
                  >
                    {/* Particules dorées flottantes */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                      {Array.from({ length: 20 }).map((_, i) => (
                        <div
                          key={i}
                          className="absolute w-1 h-1 rounded-full bg-vault-gold/60 animate-vault-float"
                          style={{
                            left: `${5 + (i * 4.5) % 90}%`,
                            top: `${10 + (i * 7) % 80}%`,
                            animationDelay: `${(i * 0.3) % 4}s`,
                            animationDuration: `${5 + (i % 3)}s`,
                          }}
                        />
                      ))}
                    </div>

                    <motion.div
                      animate={pinError ? { x: [0, -8, 8, -6, 6, 0] } : {}}
                      transition={{ duration: 0.5 }}
                      className="relative z-10 w-full max-w-sm"
                    >
                      <div className="backdrop-blur-xl bg-white/90 border border-vault-gold/30 rounded-2xl shadow-2xl shadow-vault-glow p-8 relative overflow-hidden">
                        {/* Explosion de particules au déverrouillage */}
                        {isUnlocking && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            {Array.from({ length: 12 }).map((_, i) => (
                              <motion.div
                                key={i}
                                initial={{ opacity: 1, scale: 0, x: 0, y: 0 }}
                                animate={{
                                  opacity: 0,
                                  scale: 1,
                                  x: Math.cos((i / 12) * Math.PI * 2) * 80,
                                  y: Math.sin((i / 12) * Math.PI * 2) * 80,
                                }}
                                transition={{ duration: 0.6, ease: 'easeOut' }}
                                className="absolute w-2 h-2 rounded-full bg-vault-gold"
                              />
                            ))}
                          </div>
                        )}
                        <div className="flex flex-col items-center relative z-10">
                          {/* Halo lumineux diffus derrière l'icône */}
                          <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-32 h-32 rounded-full bg-vault-gold/10 blur-2xl pointer-events-none" />
                          <div className="relative mb-6">
                            {/* Anneau de progression circulaire + halo doré */}
                            <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                              <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(212,175,55,0.2)" strokeWidth="4" />
                              <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(148,163,184,0.2)" strokeWidth="6" />
                              <motion.circle
                                cx="50"
                                cy="50"
                                r="44"
                                fill="none"
                                stroke="url(#vaultGradient)"
                                strokeWidth="6"
                                strokeLinecap="round"
                                strokeDasharray={276}
                                strokeDashoffset={276 - (276 * pinProgress) / 100}
                                style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                              />
                              <defs>
                                <linearGradient id="vaultGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor="#d4af37" />
                                  <stop offset="50%" stopColor="#f4e4bc" />
                                  <stop offset="100%" stopColor="#b8860b" />
                                </linearGradient>
                              </defs>
                            </svg>
                            {/* Cercle sombre + cadenas doré */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <motion.div
                                animate={(isUnlocking || isSettingPin) ? { scale: [1, 1.1, 1] } : {}}
                                transition={{ duration: 0.6 }}
                                className="relative w-16 h-16 rounded-full bg-slate-800 shadow-xl flex items-center justify-center ring-2 ring-vault-gold/30"
                              >
                                <Vault className={`w-8 h-8 ${(isUnlocking || isSettingPin) ? 'text-vault-light drop-shadow-[0_0_12px_rgba(212,175,55,0.9)]' : 'text-vault-gold'}`} />
                                {(isUnlocking || isSettingPin) && (
                                  <div className="absolute inset-0 bg-vault-gold/20 rounded-full blur-lg animate-pulse" />
                                )}
                              </motion.div>
                            </div>
                          </div>

                          {vaultHasPin === null ? (
                            <div className="py-8 flex flex-col items-center gap-3">
                              <div className="w-10 h-10 border-2 border-vault-gold border-t-transparent rounded-full animate-spin" />
                              <p className="text-slate-600 text-sm">Loading...</p>
                            </div>
                          ) : showForgotPin ? (
                            <>
                              <h2 className="text-xl font-bold text-slate-800 mb-2">Forgot code?</h2>
                              <p className="text-slate-500 text-sm mb-4">Enter your password to set a new code.</p>
                              <button type="button" onClick={handleForgotPinBack} className="text-sm text-slate-500 hover:text-slate-700 mb-3">
                                ← Retour
                              </button>
                              <div className="w-full max-w-xs space-y-3">
                                <input
                                  type="password"
                                  value={resetPassword}
                                  onChange={(e) => { setResetPassword(e.target.value); setPinError(false); }}
                                  placeholder="Account password"
                                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-vault-gold focus:outline-none text-slate-900"
                                />
                                <div>
                                  <p className="text-xs text-slate-500 mb-1">New code (4 digits)</p>
                                  <div className="flex gap-2 justify-center">
                                    {[0, 1, 2, 3].map((i) => (
                                      <input
                                        key={i}
                                        type="password"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={resetNewPin[i]}
                                        onChange={(e) => {
                                          const v = e.target.value.replace(/\D/g, '').slice(-1);
                                          setResetNewPin((prev) => { const n = [...prev]; n[i] = v; return n; });
                                          setPinError(false);
                                        }}
                                        className={`w-12 h-12 rounded-lg text-center font-semibold border-2 text-slate-900 ${
                                          pinError ? 'border-red-500' : 'border-slate-200 focus:border-vault-gold'
                                        } focus:outline-none`}
                                      />
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500 mb-1">Confirm code</p>
                                  <div className="flex gap-2 justify-center">
                                    {[0, 1, 2, 3].map((i) => (
                                      <input
                                        key={i}
                                        type="password"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={resetConfirmPin[i]}
                                        onChange={(e) => {
                                          const v = e.target.value.replace(/\D/g, '').slice(-1);
                                          setResetConfirmPin((prev) => { const n = [...prev]; n[i] = v; return n; });
                                          setPinError(false);
                                        }}
                                        className={`w-12 h-12 rounded-lg text-center font-semibold border-2 text-slate-900 ${
                                          pinError ? 'border-red-500' : 'border-slate-200 focus:border-vault-gold'
                                        } focus:outline-none`}
                                      />
                                    ))}
                                  </div>
                                </div>
                                {pinError && pinErrorMsg && (
                                  <p className="text-red-600 text-sm text-center">{pinErrorMsg}</p>
                                )}
                                <button
                                  type="button"
                                  onClick={handleResetPinSubmit}
                                  disabled={isResettingPin}
                                  className="w-full py-3 bg-vault-gold text-slate-900 font-semibold rounded-xl hover:bg-vault-dark disabled:opacity-50"
                                >
                                  {isResettingPin ? 'Resetting...' : 'Set new code'}
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <h2 className="text-xl font-bold text-slate-800 mb-2">
                                {vaultHasPin
                                  ? 'Enter your PIN to unlock'
                                  : pinSetupStep === 1
                                    ? 'Set a 4-digit PIN'
                                    : 'Confirm your PIN'}
                              </h2>
                              <p className="text-slate-500 text-sm mb-6">
                                {vaultHasPin
                                  ? 'Your vault is protected by your personal PIN'
                                  : pinSetupStep === 1
                                    ? 'Choose a secret code to protect your vault'
                                    : 'Enter the same code again to confirm'}
                              </p>

                              {pinSetupStep === 2 && (
                                <button type="button" onClick={handlePinSetupBack} className="text-sm text-slate-500 hover:text-slate-700 mb-2">
                                  ← Modifier le code
                                </button>
                              )}

                              <div className="flex gap-3 justify-center mb-4">
                                {[0, 1, 2, 3].map((i) =>
                                  pinSetupStep === 2 ? (
                                    <input
                                      key={i}
                                      ref={(el) => { pinConfirmRefs.current[i] = el; }}
                                      type="password"
                                      inputMode="numeric"
                                      maxLength={1}
                                      value={pinConfirmDigits[i]}
                                      onChange={(e) => handlePinConfirmChange(i, e.target.value)}
                                      onKeyDown={(e) => handlePinConfirmKeyDown(i, e)}
                                      disabled={isSettingPin}
                                      className={`w-14 h-14 rounded-xl text-2xl text-center font-semibold bg-white/50 border-2 transition-all duration-200 focus:outline-none focus:ring-0 ${
                                        pinError
                                          ? 'border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.3)] text-slate-900'
                                          : 'border-slate-300 text-slate-900 focus:border-vault-gold focus:shadow-[0_0_20px_rgba(212,175,55,0.3)]'
                                      }`}
                                    />
                                  ) : (
                                    <input
                                      key={i}
                                      ref={(el) => { pinInputRefs.current[i] = el; }}
                                      type="password"
                                      inputMode="numeric"
                                      maxLength={1}
                                      value={pinDigits[i]}
                                      onChange={(e) => handlePinDigitChange(i, e.target.value)}
                                      onKeyDown={(e) => handlePinKeyDown(i, e)}
                                      disabled={isUnlocking}
                                      className={`w-14 h-14 rounded-xl text-2xl text-center font-semibold bg-white/50 border-2 transition-all duration-200 focus:outline-none focus:ring-0 ${
                                        pinError
                                          ? 'border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.3)] text-slate-900'
                                          : 'border-slate-300 text-slate-900 focus:border-vault-gold focus:shadow-[0_0_20px_rgba(212,175,55,0.3)]'
                                      }`}
                                    />
                                  )
                                )}
                              </div>

                              {pinError && (
                                <motion.p
                                  initial={{ opacity: 0, y: -5 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className="text-red-600 text-sm mb-2"
                                >
                                  {pinErrorMsg ?? 'Code incorrect'}
                                </motion.p>
                              )}

                              {(isUnlocking || isSettingPin) && (
                                <motion.p
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  className="text-vault-dark text-sm flex items-center gap-1 font-medium"
                                >
                                  {isSettingPin ? 'Saving' : 'Unlocking'}
                                  <span className="inline-flex gap-0.5">
                                    <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 0.6 }}>.</motion.span>
                                    <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }}>.</motion.span>
                                    <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }}>.</motion.span>
                                  </span>
                                </motion.p>
                              )}

                              {vaultHasPin && (
                                <button
                                  type="button"
                                  onClick={() => setShowForgotPin(true)}
                                  className="text-sm text-slate-500 hover:text-vault-gold mt-3"
                                >
                                  Forgot code?
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                ) : (
                <motion.div
                  key="vault-content"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  className="flex-1 flex flex-col min-h-0 overflow-hidden"
                >
                  {/* Barre d’outils coffre fort (alignée sur My files) */}
                  <div className="flex-shrink-0 px-4 py-2.5 border-b border-slate-200/80 bg-white flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-slate-700">Vault</span>
                    <span className="text-slate-400">/</span>
                    <span className="text-sm text-slate-600">Protected content</span>
                    <div className="flex-1" />
                    <div className="flex items-center gap-2">
                      <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                        <button type="button" onClick={() => setViewMode('grid')} className={`p-1.5 ${viewMode === 'grid' ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:bg-slate-50'}`} title="Grid"><LayoutGrid className="w-4 h-4" /></button>
                        <button type="button" onClick={() => setViewMode('list')} className={`p-1.5 ${viewMode === 'list' ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:bg-slate-50'}`} title="List"><List className="w-4 h-4" /></button>
                      </div>
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">
                        <Upload className="w-4 h-4" /> Fichiers
                      </button>
                      <button type="button" onClick={() => folderInputRef.current?.click()} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">
                        <FolderPlus className="w-4 h-4" /> Dossier
                      </button>
                      <button type="button" onClick={() => setShowNewFolder(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700">
                        <FolderPlus className="w-4 h-4" /> New folder
                      </button>
                    </div>
                    <div className="w-full flex items-center justify-between mt-1 pt-2 border-t border-slate-100">
                      <span className="text-xs text-emerald-600 font-medium inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Unlocked
                      </span>
                      <button type="button" onClick={handleLockVault} className="text-xs text-slate-500 hover:text-amber-600 font-medium">
                        Verrouiller le coffre fort
                      </button>
                    </div>
                  </div>

                  {/* Zone contenu + liste */}
                  <div
                    className={`flex-1 overflow-auto p-4 md:p-6 transition-colors ${isDragging ? 'bg-amber-50/50' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                  >
                    {showNewFolder && (
                      <div className="mb-4 p-4 bg-white rounded-xl border border-slate-200 space-y-3">
                        <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Folder name" className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-full max-w-xs" onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()} autoFocus />
                        <div className="flex flex-wrap gap-2 items-center">
                          <select value={newFolderCategory} onChange={(e) => { setNewFolderCategory(e.target.value); setNewFolderSubcategory(''); }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
                            <option value="">Category (optional)</option>
                            {categories.map((c) => (
                              <option key={c.id} value={c.id}>{c.label}</option>
                            ))}
                          </select>
                          {newFolderCategory && (
                            <select value={newFolderSubcategory} onChange={(e) => setNewFolderSubcategory(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
                              <option value="">Subcategory</option>
                              {categories.find((c) => c.id === newFolderCategory)?.subcategories.map((s) => (
                                <option key={s.id} value={s.id}>{s.label}</option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={handleCreateFolder} className="px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700">Create</button>
                          <button type="button" onClick={() => { setShowNewFolder(false); setNewFolderName(''); setNewFolderCategory(''); setNewFolderSubcategory(''); }} className="p-2 text-slate-500 hover:text-slate-700"><X className="w-4 h-4" /></button>
                        </div>
                      </div>
                    )}
                    {uploading && (
                      <div className="mb-4 flex items-center gap-3 py-2 px-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-600">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} /></div>
                        <span>{uploadProgress}%</span>
                      </div>
                    )}
                    {error && (
                      <div className="mb-4 py-2 px-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between gap-2">
                        <span>{error}</span>
                        <button type="button" onClick={() => setError(null)} className="p-1 rounded hover:bg-red-100 text-red-600" aria-label="Close"><X className="w-4 h-4" /></button>
                      </div>
                    )}

                    {/* Liste des fichiers et dossiers du coffre fort */}
                    {loading ? (
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-sm text-slate-600">Loading...</div>
                    ) : error ? (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>
                    ) : filteredAndSortedItems.length === 0 ? (
                      <div className="py-12 text-center text-slate-500 text-sm rounded-xl bg-slate-50 border border-dashed border-slate-200">
                        No files or folders in the vault. Add some with the buttons above, or move items to "Vault" from My files (⋮ menu → Change visibility).
                      </div>
                    ) : viewMode === 'grid' ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {filteredAndSortedItems.map((item, idx) => (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.35, delay: idx * 0.05, ease: 'easeOut' }}
                            className="relative group bg-white rounded-xl border border-slate-200 p-4 hover:border-amber-200 hover:shadow-md transition-all"
                          >
                            {item.type === 'folder' ? (
                              <button type="button" onClick={() => item.type === 'folder' && handleFolderClick(item.id, item.name)} className="w-full text-left">
                                <Folder className="w-12 h-12 text-amber-500 mb-2" />
                                <p className="font-medium text-slate-900 truncate">{item.name}</p>
                              </button>
                            ) : (
                              <div
                                role={isVideoFile(item) ? 'button' : undefined}
                                tabIndex={isVideoFile(item) ? 0 : undefined}
                                className={isVideoFile(item) ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500/50 rounded' : ''}
                                onDoubleClick={() => item.type === 'file' && isVideoFile(item) && handleVideoView(item)}
                                onKeyDown={(e) => isVideoFile(item) && e.key === 'Enter' && handleVideoView(item)}
                                title={isVideoFile(item) ? 'Double-clic pour regarder' : undefined}
                              >
                                {isVideoFile(item) ? <Video className="w-12 h-12 text-emerald-500 mb-2" /> : <FileText className="w-12 h-12 text-slate-400 mb-2" />}
                                <p className="font-medium text-slate-900 truncate">{item.name}</p>
                                <p className="text-xs text-slate-500 mt-0.5">{formatSize(item.size)} • {formatDate(item.updatedAt)}</p>
                              </div>
                            )}
                            <div className="mt-2 flex flex-wrap items-center justify-between gap-1">
                              <div className="flex flex-wrap gap-1">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border bg-vault-light/30 text-vault-dark border-vault-gold/50">
                                  <Lock className="w-3.5 h-3.5" />
                                  Vault
                                </span>
                                {item.type === 'folder' && renderCategoryBadge(item)}
                              </div>
                              <div className="relative" data-vault-menu>
                                <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === item.id ? null : item.id); }} className="p-1 rounded hover:bg-slate-100">
                                  <MoreVertical className="w-4 h-4 text-slate-500" />
                                </button>
                                {menuOpenId === item.id && (
                                  <div className="absolute right-0 top-full mt-1 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-[180px]">
                                    {item.type === 'file' && isVideoFile(item) && (
                                      <button type="button" onClick={() => handleVideoView(item)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Play className="w-4 h-4" /> Regarder</button>
                                    )}
                                    <button type="button" onClick={() => item.type === 'file' && handleDownload(item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Download className="w-4 h-4" /> Download</button>
                                    <div className="relative">
                                      <button type="button" onClick={() => setPermissionSubMenuId(permissionSubMenuId === item.id ? null : item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Lock className="w-4 h-4" /> Change visibility</button>
                                      {permissionSubMenuId === item.id && (
                                        <div className="absolute left-full top-0 ml-0.5 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-30 min-w-[140px]">
                                          {(['private', 'shared', 'public', 'vault'] as PermissionLevel[]).map((perm) => (
                                            <button key={perm} type="button" onClick={() => handleUpdatePermission(item, perm)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left">
                                              {renderPermissionBadge(perm)}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <div className="relative">
                                      <button type="button" onClick={() => setFolderCategoryMenuId(folderCategoryMenuId === item.id ? null : item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Folder className="w-4 h-4" /> Category</button>
                                      {folderCategoryMenuId === item.id && (
                                        <div className="absolute left-full top-0 ml-0.5 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-30 min-w-[160px] max-h-64 overflow-y-auto">
                                          <button type="button" onClick={() => handleUpdateFolderCategory(item, '', '')} className="w-full px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 text-left">Aucune</button>
                                          {categories.map((cat) => (
                                            <div key={cat.id}>
                                              <button type="button" onClick={() => handleUpdateFolderCategory(item, cat.id, '')} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left font-medium">{cat.label}</button>
                                              {cat.subcategories.map((sub) => (
                                                <button key={sub.id} type="button" onClick={() => handleUpdateFolderCategory(item, cat.id, sub.id)} className="w-full pl-6 pr-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 text-left">— {sub.label}</button>
                                              ))}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <button type="button" onClick={() => { setMenuOpenId(null); setShareModalItem(item); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"><Share2 className="w-4 h-4" /> Partager</button>
                                    <button type="button" onClick={() => handleDelete(item)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left"><Trash2 className="w-4 h-4" /> Supprimer</button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="text-left py-3 px-4 font-semibold text-slate-700">Name</th>
                              <th className="text-left py-3 px-4 font-semibold text-slate-700">Visibility</th>
                              <th className="text-left py-3 px-4 font-semibold text-slate-700">Category</th>
                              <th className="text-left py-3 px-4 font-semibold text-slate-700">Size</th>
                              <th className="text-left py-3 px-4 font-semibold text-slate-700">Modified</th>
                              <th className="w-10" />
                            </tr>
                          </thead>
                          <tbody>
                            {filteredAndSortedItems.map((item) => (
                              <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                                <td className="py-3 px-4">
                                  {item.type === 'folder' ? (
                                    <button type="button" onClick={() => handleFolderClick(item.id, item.name)} className="flex items-center gap-2 font-medium text-slate-900 hover:text-emerald-600">
                                      <Folder className="w-4 h-4 text-amber-500" />
                                      {item.name}
                                    </button>
                                  ) : (
                                    <span className="flex items-center gap-2 text-slate-900">
                                      <FileText className="w-4 h-4 text-slate-400" />
                                      {item.name}
                                    </span>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border bg-vault-light/30 text-vault-dark border-vault-gold/50">
                                    <Lock className="w-3.5 h-3.5" />
                                    Vault
                                  </span>
                                </td>
                                <td className="py-3 px-4">{item.type === 'folder' ? renderCategoryBadge(item) || '—' : '—'}</td>
                                <td className="py-3 px-4 text-slate-600">{formatSize(item.size)}</td>
                                <td className="py-3 px-4 text-slate-600">{formatDate(item.updatedAt)}</td>
                                <td className="py-3 px-4">
                                  <div className="relative" data-vault-menu>
                                    <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === item.id ? null : item.id); }} className="p-1 rounded hover:bg-slate-200">
                                      <MoreVertical className="w-4 h-4 text-slate-500" />
                                    </button>
                                    {menuOpenId === item.id && (
                                      <div className="absolute right-0 top-full mt-1 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-[180px]">
                                        {item.type === 'file' && isVideoFile(item) && (
                                          <button type="button" onClick={() => handleVideoView(item)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Play className="w-4 h-4" /> Regarder</button>
                                        )}
                                        <button type="button" onClick={() => item.type === 'file' && handleDownload(item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Download className="w-4 h-4" /> Download</button>
                                        <div className="relative">
                                          <button type="button" onClick={() => setPermissionSubMenuId(permissionSubMenuId === item.id ? null : item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Lock className="w-4 h-4" /> Change visibility</button>
                                          {permissionSubMenuId === item.id && (
                                            <div className="absolute left-full top-0 ml-0.5 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-30 min-w-[140px]">
                                              {(['private', 'shared', 'public', 'vault'] as PermissionLevel[]).map((perm) => (
                                                <button key={perm} type="button" onClick={() => handleUpdatePermission(item, perm)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left">
                                                  {renderPermissionBadge(perm)}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                        <button type="button" onClick={() => setFolderCategoryMenuId(folderCategoryMenuId === item.id ? null : item.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Folder className="w-4 h-4" /> Category</button>
                                        {folderCategoryMenuId === item.id && (
                                          <div className="absolute left-full top-0 ml-0.5 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-30 min-w-[160px] max-h-64 overflow-y-auto">
                                            <button type="button" onClick={() => handleUpdateFolderCategory(item, '', '')} className="w-full px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 text-left">Aucune</button>
                                            {categories.map((cat) => (
                                              <div key={cat.id}>
                                                <button type="button" onClick={() => handleUpdateFolderCategory(item, cat.id, '')} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left font-medium">{cat.label}</button>
                                                {cat.subcategories.map((sub) => (
                                                  <button key={sub.id} type="button" onClick={() => handleUpdateFolderCategory(item, cat.id, sub.id)} className="w-full pl-6 pr-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 text-left">— {sub.label}</button>
                                                ))}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        <button type="button" onClick={() => { setMenuOpenId(null); setShareModalItem(item); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"><Share2 className="w-4 h-4" /> Partager</button>
                                        <button type="button" onClick={() => handleDelete(item)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left"><Trash2 className="w-4 h-4" /> Supprimer</button>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {section === 'archives' && (
            <div className="flex-1 overflow-auto p-6">
              <div className="bg-white rounded-xl border border-slate-200 p-8">
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Archives</h2>
                <p className="text-slate-600 text-sm mb-6">Infrequent Access storage. On-demand retrieval.</p>
                <div className="text-center py-12 text-slate-500 text-sm">Aucune archive.</div>
              </div>
            </div>
          )}
        </main>
      </div>

      {shareModalItem && (
        <VaultSphereShareModal
          item={{ id: shareModalItem.id, name: shareModalItem.name, type: shareModalItem.type }}
          onClose={() => setShareModalItem(null)}
          onSuccess={fetchItems}
        />
      )}
      {detailsModalItem && (
        <VaultSphereDetailsModal
          item={{ id: detailsModalItem.id, name: detailsModalItem.name, type: detailsModalItem.type }}
          onClose={() => setDetailsModalItem(null)}
        />
      )}

      {/* Modal lecteur vidéo */}
      {videoPlayerItem && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100]" onClick={closeVideoPlayer}>
          <div className="bg-slate-900 rounded-xl shadow-2xl max-w-4xl w-full mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 bg-slate-800">
              <h3 className="font-semibold text-white truncate">{videoPlayerItem.name}</h3>
              <button type="button" onClick={closeVideoPlayer} className="p-2 text-slate-400 hover:text-white rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 bg-black aspect-video flex items-center justify-center min-h-[300px]">
              {videoPlayerLoading ? (
                <div className="flex flex-col items-center gap-3 text-slate-400">
                  <div className="w-12 h-12 border-2 border-slate-500 border-t-white rounded-full animate-spin" />
                  <span>Loading video...</span>
                </div>
              ) : videoPlayerUrl ? (
                <video
                  src={videoPlayerUrl}
                  controls
                  autoPlay
                  className="max-w-full max-h-[70vh] w-full"
                  onEnded={() => {}}
                />
              ) : (
                <p className="text-slate-400">Unable to load the video</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VaultSpherePage;
